/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const UpdateUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [documents, setDocuments] = useState({ aadhar: null, pan: null });
    const [loanData, setLoanData] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            setLoading(true);
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/';

                // 1. Fetch full customer records
                const listRes = await axios.get(`${backendUrl}customer`);
                let cust = null;
                if (listRes?.data && Array.isArray(listRes.data)) {
                    cust = listRes.data.find(c => parseInt(c.id) === parseInt(id));
                } else if (listRes?.data?.data && Array.isArray(listRes.data.data)) {
                    cust = listRes.data.data.find(c => parseInt(c.id) === parseInt(id));
                }

                if (cust) {
                    setFormData(cust);
                    if (cust.uploaded_documents) {
                        try {
                            const parsedDocs = typeof cust.uploaded_documents === 'string' ? JSON.parse(cust.uploaded_documents) : cust.uploaded_documents;
                            setDocuments({
                                aadhar: parsedDocs?.aadhar || null,
                                pan: parsedDocs?.pan || null
                            });
                        } catch (e) {
                            console.error("Could not parse embedded document blob keys", e);
                        }
                    }
                } else {
                    try {
                        const singleRes = await axios.get(`${backendUrl}customer/one/${id}`);
                        if (singleRes?.data) {
                            const singleCust = singleRes.data?.data || singleRes.data;
                            setFormData(singleCust);
                        }
                    } catch (singleErr) {
                        console.error("Single resource fetch failed:", singleErr);
                    }
                }

                // 2. Fetch linked loan payload metrics
                try {
                    const loansRes = await axios.get(`${backendUrl}loans-data`);
                    if (loansRes?.data && Array.isArray(loansRes.data)) {
                        const matchedLoan = loansRes.data.find(l => parseInt(l.loan_user_id) === parseInt(id));
                        if (matchedLoan) {
                            setLoanData(matchedLoan);
                        }
                    }
                } catch (loanErr) {
                    console.log("No explicit linked loan configurations detected yet.");
                }

            } catch (err) {
                console.error("Failed to load user info:", err);
                toast.error("Error retrieving unified borrower record profile.");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchUserDetails();
        }
    }, [id]);

    const labelStyle = {
        fontSize: '12px',
        color: '#64748B',
        fontWeight: '700',
        textTransform: 'uppercase',
        display: 'block',
        marginBottom: '4px'
    };

    const valueCardStyle = {
        backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: '10px',
        padding: '12px 16px',
        fontSize: '15px',
        color: '#0F172A',
        fontWeight: '600',
        wordBreak: 'break-word',
        minHeight: '46px',
        display: 'flex',
        alignItems: 'center'
    };

    const sectionTitleStyle = {
        fontSize: '18px',
        fontWeight: '800',
        color: '#1E293B',
        borderBottom: '2px solid #E2E8F0',
        paddingBottom: '10px',
        marginBottom: '20px',
        marginTop: '30px'
    };

    const getLoanFrequencyDisplay = (loan) => {
        if (!loan) return "—";
        const paidCount = parseInt(loan.emi_paid_count || 0);
        const remCount = parseInt(loan.remaining_emi_count || 0);
        const rawTenure = parseInt(loan.loan_tenure_months || 0);

        let totalTenure = loan.total_tenure_count || (paidCount + remCount) || rawTenure;
        let emiFreq = loan.emi_frequency || 'Monthly';
        let unit = emiFreq === 'Daily' ? 'Days' : emiFreq === 'Yearly' ? 'Years' : 'Months';

        return `${totalTenure} ${unit}`;
    };

    if (loading) {
        return (
            <div className="container-fluid mt-5 pt-5 text-center">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status" />
                <h5 className="mt-3 font-weight-bold text-secondary">Loading Integrated Borrower Profile...</h5>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Top Navigation Row */}
            <div className="d-flex justify-content-between alignItems-center mb-4 px-2">
                <button
                    className="btn btn-sm font-weight-bold"
                    style={{ backgroundColor: '#F1F5F9', color: '#475569', borderRadius: '8px', padding: '8px 16px' }}
                    onClick={() => navigate('/kyc-users')}
                >
                    ← Return to User List
                </button>
                <button
                    className="btn btn-primary font-weight-bold shadow-sm d-flex alignItems-center"
                    style={{ borderRadius: '8px', padding: '10px 24px', fontSize: '15px', backgroundColor: '#0284C7', border: 0 }}
                    onClick={() => navigate(`/users/edit/${id}`)}
                >
                    <span className="mr-2" style={{ fontSize: '16px' }}>✏️</span> Edit Profile Details
                </button>
            </div>

            {/* Profile Overview Banner */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', color: 'white' }}>
                <div className="card-body p-4 p-md-5">
                    <div className="d-flex flex-column flex-md-row justify-content-between alignItems-md-center">
                        <div>
                            <span className="badge badge-info mb-2 font-weight-bold px-3 py-1" style={{ backgroundColor: 'rgba(255,255,255,0.15)', fontSize: '12px' }}>
                                ORIGINATION LEDGER ID #{formData.id || id}
                            </span>
                            <h2 className="font-weight-bold mb-1" style={{ fontSize: '28px' }}>
                                {formData.name || ''} {formData.last_name || ''}
                            </h2>
                            <p className="mb-0 text-light" style={{ opacity: 0.8, fontSize: '14px' }}>
                                Account Status: <strong className={formData.is_active ? "text-success" : "text-warning"}>
                                    {formData.is_active ? "✓ VERIFIED & ACTIVE" : "⏳ PENDING / SUSPENDED"}
                                </strong>
                            </p>
                        </div>
                        <div className="mt-3 mt-md-0 text-md-right">
                            <span className="small d-block text-light" style={{ opacity: 0.7 }}>Registered Mobile Line</span>
                            <h4 className="font-weight-bold mb-0 text-info" style={{ letterSpacing: '0.5px' }}>
                                {formData.mobile || 'N/A'}
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comprehensive Single-Page Scrollable Profile Card */}
            <div className="card border-0 shadow-sm bg-white p-4 p-md-5" style={{ borderRadius: '20px' }}>
                <div className="alert alert-info border-0 mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E3A8A', borderRadius: '12px' }}>
                    <strong className="d-block mb-1">ℹ️ Unified Borrower Profile View (Single Page)</strong>
                    Below is the comprehensive, unbroken history of all fields provided during customer onboarding. Scroll down to view documentation assets, bank mappings, and real-time active loan rules. Click &apos;Edit Profile Details&apos; above to adjust field items.
                </div>

                {/* SECTION 1: Personal & Lineage Details */}
                <div style={{ ...sectionTitleStyle, marginTop: 0 }}>
                    <span className="mr-2">👤</span> Step 1: Personal & Demographics Overview
                </div>
                <div className="row">
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>First Name</span>
                        <div style={valueCardStyle}>{formData.name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Last Name</span>
                        <div style={valueCardStyle}>{formData.last_name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Father&apos;s Name</span>
                        <div style={valueCardStyle}>{formData.father_name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Mother&apos;s Name</span>
                        <div style={valueCardStyle}>{formData.mother_name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Date of Birth</span>
                        <div style={valueCardStyle}>{formData.dob || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Loan Category Mapping</span>
                        <div style={valueCardStyle}>
                            <span className="badge badge-primary px-2 py-1">{formData.loan_category || 'Individual'}</span>
                        </div>
                    </div>
                </div>

                {/* SECTION 2: Contact Matrix & Address */}
                <div style={sectionTitleStyle}>
                    <span className="mr-2">📍</span> Step 2: Contact Attributes & Domicile
                </div>
                <div className="row">
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Verified Mobile Phone</span>
                        <div style={valueCardStyle}>{formData.mobile || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Email Gateway Address</span>
                        <div style={valueCardStyle}>{formData.email || '—'}</div>
                    </div>
                    <div className="col-12 mb-4">
                        <span style={labelStyle}>Street Residence Address</span>
                        <div style={{ ...valueCardStyle, minHeight: '80px', whiteSpace: 'pre-wrap' }}>
                            {formData.address || '—'}
                        </div>
                    </div>
                    <div className="col-md-4 mb-4">
                        <span style={labelStyle}>City</span>
                        <div style={valueCardStyle}>{formData.city || '—'}</div>
                    </div>
                    <div className="col-md-4 mb-4">
                        <span style={labelStyle}>State / Province</span>
                        <div style={valueCardStyle}>{formData.state || '—'}</div>
                    </div>
                    <div className="col-md-4 mb-4">
                        <span style={labelStyle}>Postal Zip Code</span>
                        <div style={valueCardStyle}>{formData.zip_code || '—'}</div>
                    </div>
                </div>

                {/* SECTION 3: Identity Verification & Cards */}
                <div style={sectionTitleStyle}>
                    <span className="mr-2">📋</span> Step 3: Identity Verification Keys & Uploaded Documents
                </div>
                <div className="row">
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Extracted PAN Identity Key</span>
                        <div style={{ ...valueCardStyle, color: '#0369A1', fontWeight: '700', letterSpacing: '1px' }}>
                            {formData.pan_number || '—'}
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Extracted Aadhar Number</span>
                        <div style={{ ...valueCardStyle, color: '#0369A1', fontWeight: '700', letterSpacing: '1px' }}>
                            {formData.aadhaar_number || '—'}
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Stated Monthly Take-home Pay</span>
                        <div style={{ ...valueCardStyle, color: '#059669', fontWeight: '700' }}>
                            ₹{formData.monthly_income || '0'}
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Onboarding Verification Status</span>
                        <div style={valueCardStyle}>
                            <strong className={formData.is_active ? "text-success" : "text-danger"}>
                                {formData.is_active ? "✓ VERIFIED CLEARANCE ACTIVE" : "⚠️ UNVERIFIED / SUSPENDED"}
                            </strong>
                        </div>
                    </div>

                    {/* Rendered Card Images */}
                    <div className="col-12 mt-3">
                        <span style={labelStyle}>Uploaded Government Cards Preview</span>
                        <div className="row mt-2">
                            <div className="col-sm-6 text-center mb-3">
                                <div className="p-2 border rounded bg-light">
                                    <span className="small font-weight-bold text-muted d-block mb-2">Aadhar Card Copy</span>
                                    {documents.aadhar ? (
                                        <img
                                            src={documents.aadhar}
                                            alt="Aadhar Card"
                                            className="img-fluid rounded shadow-sm"
                                            style={{ maxHeight: '200px', objectFit: 'contain', width: '100%' }}
                                        />
                                    ) : (
                                        <div className="p-4 text-muted font-italic small">No Aadhar Photo Provided</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-sm-6 text-center mb-3">
                                <div className="p-2 border rounded bg-light">
                                    <span className="small font-weight-bold text-muted d-block mb-2">PAN Card Copy</span>
                                    {documents.pan ? (
                                        <img
                                            src={documents.pan}
                                            alt="PAN Card"
                                            className="img-fluid rounded shadow-sm"
                                            style={{ maxHeight: '200px', objectFit: 'contain', width: '100%' }}
                                        />
                                    ) : (
                                        <div className="p-4 text-muted font-italic small">No PAN Photo Provided</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECTION 4: Remittance Line Mappings */}
                <div style={sectionTitleStyle}>
                    <span className="mr-2">🏦</span> Step 4: Bank Remittance Mappings
                </div>
                <div className="row">
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Bank Institution Name</span>
                        <div style={valueCardStyle}>{formData.bank_name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Account Holder Name</span>
                        <div style={valueCardStyle}>{formData.account_holder_name || '—'}</div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>Authorized Account Number</span>
                        <div style={{ ...valueCardStyle, fontFamily: 'monospace', fontSize: '16px', letterSpacing: '1px', fontWeight: '800' }}>
                            {formData.bank_account_number || '—'}
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <span style={labelStyle}>IFSC Interbank Key</span>
                        <div style={{ ...valueCardStyle, textTransform: 'uppercase' }}>{formData.ifsc_code || '—'}</div>
                    </div>
                </div>

                {/* SECTION 5: Associated Active Loan Specs */}
                <div style={sectionTitleStyle}>
                    <span className="mr-2">💰</span> Active Loan System Allocations
                </div>
                {loanData ? (
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: '#ECFEFF', borderColor: '#CFFAFE' }}>
                        <div className="row">
                            <div className="col-sm-6 col-md-3 mb-3 mb-md-0">
                                <span className="small text-muted d-block font-weight-bold">Authorized Loan Amount</span>
                                <strong style={{ fontSize: '20px', color: '#0891B2' }}>₹{loanData.loan_amount}</strong>
                            </div>
                            <div className="col-sm-6 col-md-3 mb-3 mb-md-0">
                                <span className="small text-muted d-block font-weight-bold">Tenure Schedule</span>
                                <strong style={{ fontSize: '20px', color: '#0F172A' }}>{getLoanFrequencyDisplay(loanData)}</strong>
                            </div>
                            <div className="col-sm-6 col-md-3 mb-3 mb-sm-0">
                                <span className="small text-muted d-block font-weight-bold">Base Interest Rate</span>
                                <strong style={{ fontSize: '20px', color: '#B45309' }}>{loanData.interest_rate || 0}% p.a.</strong>
                            </div>
                            <div className="col-sm-6 col-md-3">
                                <span className="small text-muted d-block font-weight-bold">Current State</span>
                                <span className="badge badge-success px-3 py-1 mt-1 text-uppercase" style={{ fontSize: '13px' }}>
                                    {loanData.loan_status || 'Active'}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 rounded text-center bg-light text-muted font-italic">
                        No customized recurring loan specifications or collection schedules initiated for this user.
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateUser;
